import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { App } from '@df/core/app';
import { NICmsContentComponent } from '@icms/core/content';
import {
    ICmsContentComponentValidationService,
    IValidtionEvent
} from '@icms/core/content/component/icms-content-component-validation.service';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { merge } from 'rxjs/internal/observable/merge';
import { Subscription } from 'rxjs/internal/Subscription';
import { isCustomerAllowed, NCustomerGroups } from './customer-groups';

@Injectable({
    providedIn: 'root'
})
export class CustomerGroupsService {
    /**
     * Cache for condition check
     */
    protected stateMap = new Map<string, BehaviorSubject<boolean>>();

    protected app = inject(App);
    protected destroyRef = inject(DestroyRef);

    protected iCmsContentComponentValidationService = inject(ICmsContentComponentValidationService);
    private subsriptions = new Subscription();

    appInitializer(): void {
        const dataObjects = new Map<
            NICmsContentComponent.IData,
            {
                stateSubscription: Subscription;
                visibility: NCustomerGroups.TId | NCustomerGroups.TId[];
            }
        >();

        this.subsriptions.add(
            this.iCmsContentComponentValidationService.validation$.subscribe((event: IValidtionEvent) => {
                const data = event.data;
                const visibility = data?.['additional']?.visibility;
                const dataObject = dataObjects.get(data);

                if (dataObject && dataObject.visibility !== visibility) {
                    dataObject.stateSubscription.unsubscribe();
                    dataObjects.delete(data);
                }

                if (visibility) {
                    const state$ = this.getState(visibility);

                    if (!dataObject && state$) {
                        let lastState = state$.value;

                        const stateSubscription = state$.subscribe(state => {
                            if (lastState !== state) {
                                lastState = state;

                                this.iCmsContentComponentValidationService.getObjectHook(data, true)?.trigger$.next();
                            }
                        });

                        dataObjects.set(data, {
                            stateSubscription,
                            visibility
                        });
                    }

                    if (!state$?.value) {
                        event.error = {
                            copy: 'Not allowed for current group ID',
                            level: 'info'
                        };
                    }
                }
            })
        );
    }

    isAllowed(group: NCustomerGroups.TId | NCustomerGroups.TId[]): boolean {
        const allowed = this.getAllowed(group);

        if (!this.app.client.isCms && allowed.length) {
            return isCustomerAllowed(allowed, this.app.customer.model.data?.group_id);
        }

        return true;
    }

    getAllowed(group: NCustomerGroups.TId | NCustomerGroups.TId[]): NCustomerGroups.TId[] {
        const groups = group ? (Array.isArray(group) ? group : [group]) : [];

        return (
            groups.filter(
                i =>
                    typeof i === 'number' ||
                    i === NCustomerGroups.NOT_LOGGED_IN_CUSTOMER_OPTION.key ||
                    i === NCustomerGroups.ALL_CUSTOMER_OPTION.key
            ) || []
        );
    }

    getState(group: NCustomerGroups.TId | NCustomerGroups.TId[]): BehaviorSubject<boolean> | undefined {
        const key = Array.isArray(group) && group.length ? group.join('.') : typeof group === 'number' ? `${group}` : undefined;

        if (key && !this.stateMap.has(key)) {
            if (!key) {
                this.stateMap.set(key, new BehaviorSubject<boolean>(true));
            } else {
                const state$ = new BehaviorSubject<boolean>(false);
                this.stateMap.set(key, state$);

                const condition = this.isAllowed(group);

                if (state$.value !== condition) {
                    state$.next(condition);
                }

                merge(this.app.customer.model.obs.status, this.app.client.isCms$)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(() => {
                        const condition = this.isAllowed(group);

                        if (state$.value !== condition) {
                            state$.next(condition);
                        }
                    });
            }
        }

        return key ? this.stateMap.get(key) : undefined;
    }

    ngOnDestroy(): void {
        this.subsriptions.unsubscribe();
    }
}
